import React, { useEffect, useState } from 'react';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { PracticeAttendee } from '../../../types/practice';
import { newId } from '../../../utils/practices';
interface AttendeesImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attendees: PracticeAttendee[]) => void;
}
type Row = {
  firstName: string;
  lastName: string;
  email: string;
};

// Used when the file is not a CSV (e.g. Excel): import is simulated
const sampleRows: Row[] = [{
  firstName: 'Paolo',
  lastName: 'Gentili',
  email: 'paolo.gentili@email.it'
}, {
  firstName: 'Sofia',
  lastName: 'Bartoli',
  email: 'sofia.bartoli@email.it'
}, {
  firstName: 'Matteo',
  lastName: 'Ciampi',
  email: 'matteo.ciampi@email.it'
}];

// CSV with columns Nome, Cognome, Email (separated by ";" or ",")
function parseCsv(text: string): Row[] {
  return text.split(/\r?\n/).map(line => line.split(/[;,]/).map(c => c.trim().replace(/^"|"$/g, ''))).filter(cols => cols.length >= 3 && cols[0] && !/^nome$/i.test(cols[0])).map(([firstName, lastName, email]) => ({
    firstName,
    lastName,
    email
  })).filter(r => /^\S+@\S+\.\S+$/.test(r.email));
}
export function AttendeesImportModal({
  isOpen,
  onClose,
  onConfirm
}: AttendeesImportModalProps) {
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!isOpen) return;
    setFileName('');
    setRows([]);
    setError('');
  }, [isOpen]);
  const readFile = (file: File) => {
    setFileName(file.name);
    setError('');
    if (!/\.csv$/i.test(file.name)) {
      setRows(sampleRows);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result || ''));
      setRows(parsed);
      if (parsed.length === 0) setError('Nessun partecipante valido trovato. Controlla le colonne Nome, Cognome, Email.');
    };
    reader.readAsText(file);
  };
  const confirm = () => onConfirm(rows.map(r => ({
    id: newId('att-imp'),
    ...r
  })));
  return <Modal isOpen={isOpen} onClose={onClose} title="Importa partecipanti" size="lg" data-id="element-1439" data-mp="2hele2rwfe5bl">
      <div className="space-y-4" data-id="element-1440" data-mp="2hc8mro4s2ilv">
        <p className="text-sm text-slate-500 -mt-1" data-id="element-1441" data-mp="2ddjajyvh773t">
          Carica un file con la lista dei partecipanti (colonne: Nome, Cognome, Email
          personale). I partecipanti verranno associati alla pratica.
        </p>
        <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-8 cursor-pointer hover:border-seform-orange/60 hover:bg-orange-50/30 transition-colors" data-id="element-1442" data-mp="23e28rsjchdd4">
          <Upload className="h-6 w-6 text-slate-400" data-id="element-1443" data-mp="1ufylaqosu6io" />
          <span className="text-sm font-medium text-slate-700" data-id="element-1444" data-mp="3su1oxtlds329">
            {fileName || 'Seleziona file dal computer'}
          </span>
          <span className="text-xs text-slate-500" data-id="element-1445" data-mp="3osol1p1um9xz">CSV, XLS o XLSX</span>
          <input type="file" accept=".csv,.xls,.xlsx" className="sr-only" onChange={e => {
          const file = e.target.files?.[0];
          if (file) readFile(file);
          e.target.value = '';
        }} data-id="element-1446" data-mp="1ajcjb47r127l" />
        </label>
        {error && <p className="text-xs text-red-600 font-medium" data-id="element-1447" data-mp="1oaiqlmh9s451">{error}</p>}
        {rows.length > 0 && <div data-id="element-1448" data-mp="rmur6lozn0at">
            <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2" data-id="element-1449" data-mp="rne05cwnuecp">
              <FileSpreadsheet className="h-4 w-4 text-slate-400" data-id="element-1450" data-mp="wkwcld9vzbpg" />
              Anteprima · {rows.length} partecipanti
            </p>
            <ul className="rounded-xl border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto" data-id="element-1451" data-mp="15lb08jabp2w4">
              {rows.map((r, i) => <li key={i} className="px-4 py-2 flex justify-between gap-3 text-sm" data-id="element-1452" data-mp="2c0v3zcvaat30" data-mpi={i}>
                  <span className="font-medium text-slate-800 truncate" data-id="element-1453" data-mp="2gz6elptel7ma">
                    {r.firstName} {r.lastName}
                  </span>
                  <span className="text-slate-500 truncate" data-id="element-1454" data-mp="2fl5ars5j773s">{r.email}</span>
                </li>)}
            </ul>
          </div>}
        <div className="flex justify-end gap-3 pt-2" data-id="element-1455" data-mp="2ol1xvpc9vxwq">
          <Button variant="flat" color="default" onClick={onClose} data-id="element-1456" data-mp="2kif0l8wu1ks5">
            Annulla
          </Button>
          <Button onClick={confirm} disabled={rows.length === 0} data-id="element-1457" data-mp="2yhws3w1ijc57">
            Associa {rows.length > 0 ? `${rows.length} ` : ''}partecipanti
          </Button>
        </div>
      </div>
    </Modal>;
}